import {
	AISuggestion,
	SuggestionCategory,
	SuggestionPriority,
	SuggestionActionType,
	CategoryStats,
	SuggestionFilters,
	UserContext,
	SuggestionEngineConfig,
	AISuggestionsResponse,
	GenerateSuggestionsRequest,
	UpdateSuggestionRequest,
} from './AISuggestionsTypes';

// AI Suggestions Service
export class AISuggestionsService {
	private baseUrl: string;
	private apiKey: string;

	constructor(baseUrl: string = '/api/ai-suggestions', apiKey?: string) {
		this.baseUrl = baseUrl;
		this.apiKey = apiKey || '';
	}

	// Generate AI suggestions based on user context
	async generateSuggestions(
		request: GenerateSuggestionsRequest
	): Promise<AISuggestionsResponse> {
		try {
			const response = await fetch(`${this.baseUrl}/generate`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${this.apiKey}`,
				},
				body: JSON.stringify(request),
			});

			if (!response.ok) {
				throw new Error(
					`Failed to generate suggestions: ${response.statusText}`
				);
			}

			return await response.json();
		} catch (error) {
			console.error('Error generating suggestions:', error);
			throw error;
		}
	}

	// Get existing suggestions with filters
	async getSuggestions(
		filters?: SuggestionFilters
	): Promise<AISuggestionsResponse> {
		try {
			const queryParams = new URLSearchParams();
			if (filters) {
				Object.entries(filters).forEach(([key, value]) => {
					if (value !== undefined) {
						queryParams.append(key, value.toString());
					}
				});
			}

			const response = await fetch(
				`${this.baseUrl}?${queryParams.toString()}`,
				{
					method: 'GET',
					headers: {
						Authorization: `Bearer ${this.apiKey}`,
					},
				}
			);

			if (!response.ok) {
				throw new Error(
					`Failed to get suggestions: ${response.statusText}`
				);
			}

			return await response.json();
		} catch (error) {
			console.error('Error getting suggestions:', error);
			throw error;
		}
	}

	// Update suggestion status
	async updateSuggestion(
		request: UpdateSuggestionRequest
	): Promise<AISuggestion> {
		try {
			const response = await fetch(
				`${this.baseUrl}/${request.suggestionId}`,
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${this.apiKey}`,
					},
					body: JSON.stringify(request),
				}
			);

			if (!response.ok) {
				throw new Error(
					`Failed to update suggestion: ${response.statusText}`
				);
			}

			return await response.json();
		} catch (error) {
			console.error('Error updating suggestion:', error);
			throw error;
		}
	}

	// Dismiss a suggestion
	async dismissSuggestion(suggestionId: string): Promise<void> {
		await this.updateSuggestion({
			suggestionId,
			action: 'dismiss',
		});
	}

	// Mark suggestion as read
	async markAsRead(suggestionId: string): Promise<void> {
		await this.updateSuggestion({
			suggestionId,
			action: 'mark_read',
		});
	}

	// Snooze a suggestion
	async snoozeSuggestion(
		suggestionId: string,
		duration: number
	): Promise<void> {
		await this.updateSuggestion({
			suggestionId,
			action: 'snooze',
			metadata: {
				snoozeUntil: new Date(Date.now() + duration).toISOString(),
			},
		});
	}

	// Get category statistics
	async getCategoryStats(): Promise<CategoryStats> {
		try {
			const response = await fetch(`${this.baseUrl}/stats/categories`, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${this.apiKey}`,
				},
			});

			if (!response.ok) {
				throw new Error(
					`Failed to get category stats: ${response.statusText}`
				);
			}

			return await response.json();
		} catch (error) {
			console.error('Error getting category stats:', error);
			throw error;
		}
	}

	// Get analytics
	async getAnalytics(): Promise<any> {
		try {
			const response = await fetch(`${this.baseUrl}/analytics`, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${this.apiKey}`,
				},
			});

			if (!response.ok) {
				throw new Error(
					`Failed to get analytics: ${response.statusText}`
				);
			}

			return await response.json();
		} catch (error) {
			console.error('Error getting analytics:', error);
			throw error;
		}
	}
}

// Mock Data Generator for Development
export class MockAISuggestionsService extends AISuggestionsService {
	private mockSuggestions: AISuggestion[] = [];

	constructor() {
		super();
		this.initializeMockData();
	}

	private initializeMockData(): void {
		this.mockSuggestions = this.generateMockSuggestions();
	}

	private generateMockSuggestions(): AISuggestion[] {
		const categories: SuggestionCategory[] = [
			'data',
			'projects',
			'knowledge',
			'calendar',
			'templates',
		];
		const actionTypes: SuggestionActionType[] = [
			'create',
			'update',
			'analyze',
			'schedule',
			'template',
		];
		const priorities: SuggestionPriority[] = ['high', 'medium', 'low'];

		const suggestions: AISuggestion[] = [];

		categories.forEach((category, categoryIndex) => {
			for (let i = 0; i < 3; i++) {
				const suggestion: AISuggestion = {
					id: `${category}-${i + 1}`,
					title: this.getSuggestionTitle(category, i),
					description: this.getSuggestionDescription(category, i),
					category,
					priority:
						priorities[
							Math.floor(Math.random() * priorities.length)
						],
					confidence: Math.floor(Math.random() * 40) + 60, // 60-100
					actionType:
						actionTypes[
							Math.floor(Math.random() * actionTypes.length)
						],
					metadata: {
						estimatedTime: this.getEstimatedTime(category),
						difficulty: this.getDifficulty(category),
						tags: this.getTags(category),
						relatedItems: this.getRelatedItems(category),
						context: {
							userActivity: [
								'recent_data_access',
								'project_update',
							],
							recentActions: [
								'viewed_dashboard',
								'created_document',
							],
							currentProjects: ['Project Alpha', 'Project Beta'],
							timeOfDay: 'morning',
							dayOfWeek: 'weekday',
						},
						impact: {
							productivity: Math.floor(Math.random() * 40) + 60,
							timeSaved: this.getTimeSaved(category),
							businessValue: ['low', 'medium', 'high'][
								Math.floor(Math.random() * 3)
							] as 'low' | 'medium' | 'high',
							urgency: ['low', 'medium', 'high'][
								Math.floor(Math.random() * 3)
							] as 'low' | 'medium' | 'high',
						},
					},
					createdAt: new Date(
						Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
					).toISOString(),
					expiresAt: new Date(
						Date.now() + Math.random() * 14 * 24 * 60 * 60 * 1000
					).toISOString(),
					isRead: Math.random() > 0.5,
					isDismissed: false,
				};
				suggestions.push(suggestion);
			}
		});

		return suggestions.sort((a, b) => {
			const priorityOrder = { high: 3, medium: 2, low: 1 };
			return priorityOrder[b.priority] - priorityOrder[a.priority];
		});
	}

	private getSuggestionTitle(
		category: SuggestionCategory,
		index: number
	): string {
		const titles = {
			data: [
				'Analyze Q4 Sales Performance',
				'Create Customer Segmentation Report',
				'Optimize Data Pipeline Performance',
			],
			projects: [
				'Review Project Timeline',
				'Assign Team Members to Tasks',
				'Update Project Status',
			],
			knowledge: [
				'Learn Advanced Analytics',
				'Review Industry Best Practices',
				'Create Knowledge Base Entry',
			],
			calendar: [
				'Schedule Team Meeting',
				'Block Focus Time',
				'Plan Quarterly Review',
			],
			templates: [
				'Create Project Proposal Template',
				'Design Meeting Agenda Template',
				'Build Status Report Template',
			],
		};
		return titles[category][index];
	}

	private getSuggestionDescription(
		category: SuggestionCategory,
		index: number
	): string {
		const descriptions = {
			data: [
				'Analyze sales trends and identify opportunities for improvement',
				'Segment customers based on behavior and value for targeted marketing',
				'Review and optimize data processing workflows for better performance',
			],
			projects: [
				'Review current project timeline and adjust deadlines if needed',
				'Assign team members to upcoming tasks based on availability',
				'Update project status and communicate progress to stakeholders',
			],
			knowledge: [
				'Explore advanced analytics techniques to improve data insights',
				'Research industry best practices for your current projects',
				'Document important processes and procedures for team knowledge',
			],
			calendar: [
				'Schedule a team meeting to discuss project progress',
				'Block dedicated time for deep work and focused tasks',
				'Plan and schedule quarterly business review meetings',
			],
			templates: [
				'Create a standardized template for project proposals',
				'Design a reusable meeting agenda template',
				'Build a template for regular status reports',
			],
		};
		return descriptions[category][index];
	}

	private getEstimatedTime(category: SuggestionCategory): string {
		const times = {
			data: '2-4 hours',
			projects: '1-2 hours',
			knowledge: '30-60 minutes',
			calendar: '15-30 minutes',
			templates: '1-3 hours',
		};
		return times[category];
	}

	private getDifficulty(
		category: SuggestionCategory
	): 'easy' | 'medium' | 'hard' {
		const difficulties = {
			data: 'medium' as const,
			projects: 'easy' as const,
			knowledge: 'easy' as const,
			calendar: 'easy' as const,
			templates: 'medium' as const,
		};
		return difficulties[category];
	}

	private getTags(category: SuggestionCategory): string[] {
		const tagMap = {
			data: ['analytics', 'performance', 'insights'],
			projects: ['management', 'collaboration', 'planning'],
			knowledge: ['learning', 'documentation', 'best-practices'],
			calendar: ['scheduling', 'time-management', 'meetings'],
			templates: ['productivity', 'standardization', 'workflow'],
		};
		return tagMap[category];
	}

	private getRelatedItems(category: SuggestionCategory): string[] {
		const relatedMap = {
			data: ['Sales Dashboard', 'Customer Database', 'Analytics Reports'],
			projects: ['Project Alpha', 'Team Tasks', 'Resource Planning'],
			knowledge: ['Learning Path', 'Documentation', 'Best Practices'],
			calendar: ['Team Meetings', 'Focus Time', 'Quarterly Reviews'],
			templates: ['Proposal Template', 'Meeting Agenda', 'Status Report'],
		};
		return relatedMap[category];
	}

	private getTimeSaved(category: SuggestionCategory): string {
		const timeMap = {
			data: '2-3 hours per week',
			projects: '1-2 hours per week',
			knowledge: '30 minutes per week',
			calendar: '15 minutes per week',
			templates: '1-2 hours per week',
		};
		return timeMap[category];
	}

	// Override methods to use mock data
	async generateSuggestions(
		request: GenerateSuggestionsRequest
	): Promise<AISuggestionsResponse> {
		// Simulate API delay
		await new Promise((resolve) => setTimeout(resolve, 1000));

		const filteredSuggestions = this.applyFilters(
			this.mockSuggestions,
			request.filters
		);

		return {
			suggestions: filteredSuggestions,
			total: filteredSuggestions.length,
			hasMore: false,
			analytics: {
				totalGenerated: this.mockSuggestions.length,
				totalAccepted: Math.floor(this.mockSuggestions.length * 0.3),
				totalDismissed: Math.floor(this.mockSuggestions.length * 0.1),
				acceptanceRate: 0.3,
				categoryPerformance: this.getCategoryPerformance(),
				priorityPerformance: this.getPriorityPerformance(),
				timeToAction: {},
			},
		};
	}

	async getSuggestions(
		filters?: SuggestionFilters
	): Promise<AISuggestionsResponse> {
		await new Promise((resolve) => setTimeout(resolve, 500));

		const filteredSuggestions = this.applyFilters(
			this.mockSuggestions,
			filters
		);

		return {
			suggestions: filteredSuggestions,
			total: filteredSuggestions.length,
			hasMore: false,
			analytics: {
				totalGenerated: this.mockSuggestions.length,
				totalAccepted: Math.floor(this.mockSuggestions.length * 0.3),
				totalDismissed: Math.floor(this.mockSuggestions.length * 0.1),
				acceptanceRate: 0.3,
				categoryPerformance: this.getCategoryPerformance(),
				priorityPerformance: this.getPriorityPerformance(),
				timeToAction: {},
			},
		};
	}

	async updateSuggestion(
		request: UpdateSuggestionRequest
	): Promise<AISuggestion> {
		await new Promise((resolve) => setTimeout(resolve, 300));

		const suggestionIndex = this.mockSuggestions.findIndex(
			(s) => s.id === request.suggestionId
		);
		if (suggestionIndex === -1) {
			throw new Error('Suggestion not found');
		}

		const suggestion = this.mockSuggestions[suggestionIndex];

		switch (request.action) {
			case 'mark_read':
				suggestion.isRead = true;
				break;
			case 'dismiss':
				suggestion.isDismissed = true;
				break;
			case 'snooze':
				// Handle snooze logic
				break;
			case 'customize':
				// Handle customization
				break;
		}

		if (request.metadata) {
			suggestion.metadata = {
				...suggestion.metadata,
				...request.metadata,
			};
		}

		return suggestion;
	}

	async getCategoryStats(): Promise<CategoryStats> {
		await new Promise((resolve) => setTimeout(resolve, 200));

		const stats: CategoryStats = {
			data: 0,
			projects: 0,
			knowledge: 0,
			calendar: 0,
			templates: 0,
		};

		this.mockSuggestions.forEach((suggestion) => {
			if (!suggestion.isDismissed) {
				stats[suggestion.category]++;
			}
		});

		return stats;
	}

	private applyFilters(
		suggestions: AISuggestion[],
		filters?: SuggestionFilters
	): AISuggestion[] {
		if (!filters) return suggestions;

		return suggestions.filter((suggestion) => {
			if (filters.category && suggestion.category !== filters.category)
				return false;
			if (filters.priority && suggestion.priority !== filters.priority)
				return false;
			if (filters.readStatus === 'read' && !suggestion.isRead)
				return false;
			if (filters.readStatus === 'unread' && suggestion.isRead)
				return false;
			if (filters.dismissed === false && suggestion.isDismissed)
				return false;

			return true;
		});
	}

	private getCategoryPerformance() {
		const categories: SuggestionCategory[] = [
			'data',
			'projects',
			'knowledge',
			'calendar',
			'templates',
		];
		const performance: any = {};

		categories.forEach((category) => {
			const categorySuggestions = this.mockSuggestions.filter(
				(s) => s.category === category
			);
			performance[category] = {
				generated: categorySuggestions.length,
				accepted: Math.floor(categorySuggestions.length * 0.3),
				acceptanceRate: 0.3,
			};
		});

		return performance;
	}

	private getPriorityPerformance() {
		const priorities: SuggestionPriority[] = ['high', 'medium', 'low'];
		const performance: any = {};

		priorities.forEach((priority) => {
			const prioritySuggestions = this.mockSuggestions.filter(
				(s) => s.priority === priority
			);
			performance[priority] = {
				generated: prioritySuggestions.length,
				accepted: Math.floor(prioritySuggestions.length * 0.3),
				acceptanceRate: 0.3,
			};
		});

		return performance;
	}
}

// Export singleton instance
export const aiSuggestionsService = new MockAISuggestionsService();
